import { _MiscMessages, ConfigSystemParams, DashboardProfit, MdmDashboardWidgetTitles } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DashboardStatistic } from 'src/app/core/interfaces/general/dashboard-statistic';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { IntegerResultAndPercentageChangeDTO, IntegerResultAndPercentageChangeDTO_EmptyObject } from 'src/app/core/interfaces/general/integer-result-and-percentage-change';
import { MdmDevicesIntuneStatsService } from 'src/app/core/services/utelize/system/device-management/mdm-devices-intune-stats-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { mdmUserDomainsDisplayText } from 'src/app/core/functions-generic/functions-generic-mdm';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-mdm-devices-02',
  templateUrl: './widget-row-mdm-devices-02.component.html',
  styleUrls: ['./widget-row-mdm-devices-02.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowMdmDevices02Component implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  @Input() actionButton0Show  : boolean | undefined;
  @Input() actionButton0Text  : string  | undefined;
  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() deviceClass        : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() previousSync       : boolean | undefined=false;
  @Input() useContainer       : boolean | undefined;
  @Input() showTitleHintText  : boolean | undefined;
  @Input() userDomains        : string  | undefined;
    
  @Output() actionButton0Click: EventEmitter<void                                     >=new EventEmitter<void                                     >();
  @Output() containerClosed   : EventEmitter<void                                     >=new EventEmitter<void                                     >();
  @Output() widgetClick       : EventEmitter<{widgetName: string, widgetValue: string}>=new EventEmitter<{widgetName: string, widgetValue: string}>();
  @Output() userDomainsClear  : EventEmitter<void                                     >=new EventEmitter<void                                     >();

  get userDomainsText() { return mdmUserDomainsDisplayText(this.exceptionsService, this.userDomains); }
    
  get pollingWidgets         () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_MdmDevices02,      false); }
  get showEndOfSupportWidgets() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.MdmDeviceEndOfSupportWidgetsEnabled, false); }
    
  get maxWidgetsPerRow() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxWidgetsPerRow_MdmDevices02, 4  ); }

  get statBoxMinWidth(): string {
    try {
      const raw          =this.maxWidgetsPerRow;
      const widgetsPerRow=(Number.isFinite(raw) && raw > 0) ? Math.max(1, Math.min(8, raw)) : 4;

      return (widgetsPerRow>=6) ? "180px" : "250px";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "250px";
    }
  }

  private filterCompanyID                             : number | undefined;
  private filterDate                                  : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statTitleIntuneDevicesCheckedInLast30Days   : string="Check-in (30 days)";
  private statTitleIntuneDevicesNotCheckedInLast30Days: string="No check-in (30 days)";
  private statTitleIntuneDevicesNotSupported          : string="Not supported (EOL)";
  private statTitleIntuneDevicesNotSupportedEOS       : string="Not supported (EOS)";
  private statTitleIntuneDevicesSupported             : string="Supported (EOL)";
  private statTitleIntuneDevicesSupportedEOS          : string="Supported (EOS)";
  private statTitleIntuneDevicesTotalCount            : string="Devices in MDM";
  
  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number }; refreshToken?: number; }>();
  
  public expandContainer : boolean=true;
  public statData!       : Array<DashboardStatistic>;
  public syncInfoText    : string="";
  public visibleContainer: boolean=true;

  constructor(
    private changeDetectorRef           : ChangeDetectorRef,
    private configSystemService         : ConfigSystemService,
    private exceptionsService           : ExceptionsService,
    private mdmDevicesIntuneStatsService: MdmDevicesIntuneStatsService,
    private tippyToolTipService         : TippyToolTipService,
    private widgetService               : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      
      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (((changes['deviceClass'] && !changes['deviceClass'].firstChange)
           || (changes['userDomains'] && !changes['userDomains'].firstChange)
          ) && (this.filterCompanyID ?? -1)!=-1)
        this.refreshWidgets();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refreshWidgets(): void {
    try {
      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
  
      this.selection$.next({ companyId, date, refreshToken: Date.now() });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];
                   
      let statIntuneDevicesCheckedInLast30Days: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-calendar-check",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        title         : this.statTitleIntuneDevicesCheckedInLast30Days,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_CheckedInLast30Days,
      }
                   
      let statIntuneDevicesNotCheckedInLast30Days: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-calendar-x",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        title         : this.statTitleIntuneDevicesNotCheckedInLast30Days,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_NotCheckedInLast30Days,
      }
                   
      let statIntuneDevicesNotSupported: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-x-circle",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Or not-evaluated",
        title         : this.statTitleIntuneDevicesNotSupported,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_NotSupported,
      }
                
      let statIntuneDevicesNotSupportedEOS: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-shield-x",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Or not-evaluated",
        title         : this.statTitleIntuneDevicesNotSupportedEOS,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_NotSupportedEOS,
      }
                   
      let statIntuneDevicesSupported: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-check-circle",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "OS updates available",
        title         : this.statTitleIntuneDevicesSupported,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_Supported,
      }

      let statIntuneDevicesSupportedEOS: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-check-shield",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Security patches available",
        title         : this.statTitleIntuneDevicesSupportedEOS,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_SupportedEOS,
      }
                   
      let statIntuneDevicesTotalCount: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-mobile",
        iconColour    : "info",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        title         : this.statTitleIntuneDevicesTotalCount,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_TotalCount,
      }

      this.statData.push(statIntuneDevicesTotalCount);
      this.statData.push(statIntuneDevicesCheckedInLast30Days);
      this.statData.push(statIntuneDevicesNotCheckedInLast30Days);
      this.statData.push(statIntuneDevicesSupported);
      this.statData.push(statIntuneDevicesNotSupported);

      if (this.showEndOfSupportWidgets) {
        this.statData.push(statIntuneDevicesSupportedEOS);
        this.statData.push(statIntuneDevicesNotSupportedEOS);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId   ===b.companyId  &&
          a.date.year   ===b.date.year  &&
          a.date.month  ===b.date.month &&
          a.date.day    ===b.date.day   &&
          a.refreshToken===b.refreshToken
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;
          
          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );
                    
          const intuneDevicesCheckedInLast30DaysSource=this.pollingWidgets
                                                       ? this.widgetService.getIntuneDevicesCheckedInLast30DaysUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                       : this.widgetService.getIntuneDevicesCheckedInLast30Days       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneDevicesNotCheckedInLast30DaysSource=this.pollingWidgets
                                                          ? this.widgetService.getIntuneDevicesNotCheckedInLast30DaysUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                          : this.widgetService.getIntuneDevicesNotCheckedInLast30Days       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneDevicesNotSupportedSource=this.pollingWidgets
                                                ? this.widgetService.getIntuneDevicesNotSupportedUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                : this.widgetService.getIntuneDevicesNotSupported       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneDevicesNotSupportedEOSSource=!this.showEndOfSupportWidgets
                                                   ? of(IntegerResultAndPercentageChangeDTO_EmptyObject())
                                                   : (this.pollingWidgets
                                                      ? this.widgetService.getIntuneDevicesNotSupportedEOSUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                      : this.widgetService.getIntuneDevicesNotSupportedEOS       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains));

          const intuneDevicesSupportedSource=this.pollingWidgets
                                             ? this.widgetService.getIntuneDevicesSupportedUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                             : this.widgetService.getIntuneDevicesSupported       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneDevicesSupportedEOSSource=!this.showEndOfSupportWidgets
                                                ? of(IntegerResultAndPercentageChangeDTO_EmptyObject())
                                                : (this.pollingWidgets
                                                   ? this.widgetService.getIntuneDevicesSupportedEOSUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                   : this.widgetService.getIntuneDevicesSupportedEOS       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains));

          const intuneDevicesTotalCountSource=this.pollingWidgets
                                              ? this.widgetService.getIntuneDevicesTotalCountUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                              : this.widgetService.getIntuneDevicesTotalCount       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          return combineLatest({
            intuneDevicesCheckedInLast30Days   : safeStart(intuneDevicesCheckedInLast30DaysSource,    IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesNotCheckedInLast30Days: safeStart(intuneDevicesNotCheckedInLast30DaysSource, IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesNotSupported          : safeStart(intuneDevicesNotSupportedSource,           IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesNotSupportedEOS       : safeStart(intuneDevicesNotSupportedEOSSource,        IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesSupported             : safeStart(intuneDevicesSupportedSource,              IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesSupportedEOS          : safeStart(intuneDevicesSupportedEOSSource,           IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesTotalCount            : safeStart(intuneDevicesTotalCountSource,             IntegerResultAndPercentageChangeDTO_EmptyObject()),
          });
          // .pipe(
          //   catchError((error) => {
          //     this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          //     return EMPTY; //👈 CRN_20251229 - If one fails, nothing is returned
          //   })
          // );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applyIntuneDevicesCheckedInLast30Days   (result.intuneDevicesCheckedInLast30Days);
        this.applyIntuneDevicesNotCheckedInLast30Days(result.intuneDevicesNotCheckedInLast30Days);
        this.applyIntuneDevicesNotSupported          (result.intuneDevicesNotSupported);
        this.applyIntuneDevicesNotSupportedEOS       (result.intuneDevicesNotSupportedEOS);
        this.applyIntuneDevicesSupported             (result.intuneDevicesSupported);
        this.applyIntuneDevicesSupportedEOS          (result.intuneDevicesSupportedEOS);
        this.applyIntuneDevicesTotalCount            (result.intuneDevicesTotalCount);
        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };

      this.selection$.next({ companyId, date });

      if (this.previousSync)
        this.getSyncedByInfo_Previous();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getSyncedByInfo_Previous(): void {
    try {
      const companyID: number=this.filterCompanyID ?? 0;

      if (companyID<=0) {
        this.syncInfoText="";
        return;
      }

      this.mdmDevicesIntuneStatsService.getSyncedByInfo_Previous(companyID)
      .pipe(
        catchError((error) => {
          this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          return of(null);
        })
      )
      .subscribe({
        next: data => {
          if (!data?.dateResult) {
            this.syncInfoText="Unsynced";
            this.changeDetectorRef.markForCheck();
            return;
          }

          const date     =new Date(data.dateResult);
          const pad      =(n: number) => n.toString().padStart(2, '0');
          const formatted=`${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
                          `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

          this.syncInfoText=data?.stringResult
                            ? `${formatted} by ${data.stringResult}`
                            : `${formatted}`;
          this.changeDetectorRef.markForCheck();
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyIntuneDevicesCheckedInLast30Days(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesCheckedInLast30Days) {
          stat.loading   =false,
          stat.percentage=((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit    =data.percentageChange==null
                          ? undefined
                          : (data.percentageChange==0
                             ? DashboardProfit.Equal
                             : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.showProfit=!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value     =data.result
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesNotCheckedInLast30Days(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesNotCheckedInLast30Days) {
          stat.loading        =false,
          stat.percentage     =((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit         =data.percentageChange==null
                               ? undefined
                               : (data.percentageChange==0
                                  ? DashboardProfit.Equal
                                  : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.profitUpIsGood=false;
          stat.showProfit    =!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value         =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesNotSupported(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesNotSupported) {
          stat.loading       =false,
          stat.percentage    =((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit        =data.percentageChange==null
                              ? undefined
                              : (data.percentageChange==0
                                 ? DashboardProfit.Equal
                                 : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.profitUpIsGood=false;
          stat.showProfit    =!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value         =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesNotSupportedEOS(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();

      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesNotSupportedEOS) {
          stat.loading       =false,
          stat.percentage    =((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit        =data.percentageChange==null
                              ? undefined
                              : (data.percentageChange==0
                                 ? DashboardProfit.Equal
                                 : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.profitUpIsGood=false;
          stat.showProfit    =!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value         =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyIntuneDevicesSupported(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesSupported) {
          stat.loading   =false,
          stat.percentage=((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit    =data.percentageChange==null
                          ? undefined
                          : (data.percentageChange==0
                             ? DashboardProfit.Equal
                             : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.showProfit=!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value     =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesSupportedEOS(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();

      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesSupportedEOS) {
          stat.loading   =false,
          stat.percentage=((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit    =data.percentageChange==null
                          ? undefined
                          : (data.percentageChange==0
                             ? DashboardProfit.Equal
                             : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.showProfit=!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value     =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyIntuneDevicesTotalCount(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesTotalCount) {
          stat.loading   =false,
          stat.percentage=((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit    =data.percentageChange==null
                          ? undefined
                          : (data.percentageChange==0
                             ? DashboardProfit.Equal
                             : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.showProfit=!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value     =data.result
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public trendPercent(stat: DashboardStatistic): number | null {
    try {
      if (!stat?.showProfit || stat?.percentage==null || stat.percentage=="")
        return null;

      const percent=Number(stat.percentage);

      return Number.isFinite(percent) ? percent : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  public onWidgetClick(stat: DashboardStatistic): void {
    try {
      const widgetName =stat?.widgetName ?? stat?.title ?? "";
      const widgetValue=(stat?.value ?? "") as any;

      // alert(`WidgetRowMdmDevices02Component\nWidgetName: ${widgetName}\nWidgetValue: ${widgetValue}`);

      this.widgetClick.emit({ widgetName: widgetName, widgetValue: "" + widgetValue });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public actionButton0OnClick(): void {
    try {
      this.actionButton0Click.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
